import { useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const categories = ["All", "Pulo Assistant", "Stay", "Rides"];

const posts = [
  {
    title: "Hidden Gems: Underrated Destinations You Should Visit This Year",
    category: "Pulo Assistant",
    date: "13 November, 2025",
    image: "/images/article1.jpg",
    excerpt:
      "Highlight lesser-known destinations that offer unique experiences away from crowds—cover local attractions, travel tips, and recommended accommodations for your perfect getaway.",
  },
  {
    title: "Where to Stay: A Guide to Choosing the Perfect Accommodation for Your Trip",
    category: "Stay",
    date: "13 November, 2025",
    image: "/images/article2.jpg",
    excerpt:
      "Break down types of accommodations—hotels, apartments, motels, hostels, vacation rentals—and how to find your best fit: families, couples, solo travelers, business guests.",
  },
  {
    title: "The Ultimate Packing Checklist for Every Type of Traveler",
    category: "Rides",
    date: "13 November, 2025",
    image: "/images/article3.jpg",
    excerpt:
      "Provide curated checklists for each traveler—weekend, adventure, or city breaks. Highlight carry-on essentials, tech gadgets, weather-proof basics, and travel-ready accessories.",
  },
  {
    title: "Top 10 Budget-Friendly Destinations That Feel Like Luxury",
    category: "Stay",
    date: "13 April, 2025",
    image: "/images/article4.jpg",
    excerpt:
      "Explore affordable yet luxe destinations with budget accommodation options—include currency tips, budget-saving comparisons, and insider rates.",
  },
  {
    title: "A Foodie's Travel Guide: Where to Stay and Eat Like a Local",
    category: "Pulo Assistant",
    date: "13 April, 2025",
    image: "/images/article5.jpg",
    excerpt:
      "Spotlight culinary hotspots, street food tours, and must-try local dishes. Pair neighborhoods with nearby stays and your best guide recs.",
  },
  {
    title: "Weekend Getaways: Best Short Trips Near Major Cities",
    category: "Rides",
    date: "13 April, 2025",
    image: "/images/article6.jpg",
    excerpt:
      "Create quick trips for spontaneous travelers—places 2–3 hours from popular cities, with scenic stops, things to do, and estimated budgets.",
  },
  {
    title: "Travel Smart: How to Get the Best Deals on Hotels and Rentals",
    category: "Stay",
    date: "13 April, 2025",
    image: "/images/article7.jpg",
    excerpt:
      "Share insider tips on booking during off-season, using loyalty programs, and leveraging your platform’s discounts or features.",
  },
  {
    title: "Romantic Escapes: Top Budget Friendly Destinations for Couples",
    category: "Stay",
    date: "15 April, 2025",
    image: "/images/article8.jpg",
    excerpt:
      "List destinations perfect for honeymoons, anniversaries, or quick couple retreats—highlight romantic accommodations and activities.",
  },
  {
    title: "Sustainable Travel: How to Explore the World Responsibly",
    category: "Rides",
    date: "13 April, 2025",
    image: "/images/article9.jpg",
    excerpt:
      "Discuss eco-friendly travel habits—such as choosing green-rated accommodations, supporting local communities, reducing plastic use, and mindful transportation choices.",
  },
];

function PostCard({ post }) {
  return (
    <article className=" hover:-translate-y-1 transition-transform duration-200">
      <div className="h-70">
        <img
          src={post.image}
          alt={post.title}
          className="rounded-lg w-full h-full object-cover"
        />
      </div>
      <div className="pt-4 space-y-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-2">
          <span className="font-medium">{post.date}</span>
          {/* <span className="h-1 w-1 rounded-full bg-slate-400" />
          <span>{post.category}</span> */}
        </div>
        <h3 className="text-base font-semibold text-slate-900 leading-snug">
          "{post.title}"
        </h3>
        <p className="text-sm text-slate-600 font-medium leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <hr />
          <button
            type="button"
            className="text-[#61013B] text-sm font-semibold w-full text-center"
          >
            Read article
          </button>
      </div>
    </article>
  );
}

function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredPosts = useMemo(() => {
    if (activeCategory === "All") return posts;
    return posts.filter((post) => post.category === activeCategory);
  }, [activeCategory]);

  return (
    <div className="bg-white min-h-screen">
      <Header />

      <main className="space-y-12 pb-16">
        <section className="relative w-full min-h-[20vh] sm:min-h-[50vh] xl:min-h-[85vh] flex items-center justify-center px-4 max-sm:py-6">
          <div className="absolute inset-0">
            <img
              src="/images/home-hero.jpg"
              alt="Traveler with backpack"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6 gap-4">
            <h1 className="text-3xl sm:text-5xl font-bold">
              Travel Smarter. Stay Better.
            </h1>
            <p className="max-w-2xl text-sm sm:text-lg font-medium leading-relaxed">
              Get insider advice, destination insights, and accommodation tips
              designed to help you plan seamlessly, save more, and travel with
              confidence.
            </p>
          </div>
        </section>

        <section className="w-full max-w-6xl lg:max-w-[81%] mx-auto px-4 space-y-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => {
              const isActive = activeCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                    isActive
                      ? "bg-[#61013B] text-white border-[#61013B]"
                      : "border-slate-200 text-slate-700 hover:border-[#61013B] hover:text-[#61013B]"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <PostCard key={`${post.title}-${post.date}`} post={post} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-2 pt-4">
            <button
              type="button"
              className="h-8 w-8 rounded-full border border-slate-200 text-slate-600 flex items-center justify-center"
            >
              ←
            </button>
            {[1, 2, 3].map((page) => (
              <button
                key={page}
                type="button"
                className={`h-8 w-8 rounded-full border flex items-center justify-center text-sm ${
                  page === 1
                    ? "bg-[#61013B] text-white border-[#61013B]"
                    : "border-slate-200 text-slate-700"
                }`}
              >
                {page}
              </button>
            ))}
            <div className="h-8 w-8 flex items-center justify-center text-sm text-slate-500">
              ...
            </div>
            <button
              type="button"
              className="h-8 w-10 rounded-full border border-slate-200 text-slate-700 flex items-center justify-center text-sm"
            >
              10
            </button>
            <button
              type="button"
              className="h-8 w-8 rounded-full border border-slate-200 text-slate-600 flex items-center justify-center"
            >
              →
            </button>
          </div>
        </section>


      </main>

      <Footer />
    </div>
  );
}

export default Blog;
